import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ArrowLeft, RefreshCw, Search, FileText, Package, Layers, X, Filter, Building2, Ruler, Hash, TrendingUp, ChevronDown, Check, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WOReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Work orders data
  const [workOrders, setWorkOrders] = useState([]);
  
  // Customers data (for item filtering)
  const [customers, setCustomers] = useState([]);
  
  // Dropdown open states
  const [companyOpen, setCompanyOpen] = useState(false);
  const [rmNameOpen, setRmNameOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [itemNameOpen, setItemNameOpen] = useState(false);
  
  // Selected values
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedRmName, setSelectedRmName] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedItemName, setSelectedItemName] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [woResponse, custResponse] = await Promise.all([
        axios.get(`${API}/work-orders`),
        axios.get(`${API}/wo-customers`)
      ]);
      setWorkOrders(woResponse.data || []);
      setCustomers(custResponse.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const clearFilters = () => {
    setSelectedCompany("");
    setSelectedRmName("");
    setSelectedSize("");
    setSelectedItemName("");
  };

  // Extract material usage from work orders - returns flat list with individual WO entries
  const getMaterialUsageData = () => {
    const usageList = [];
    
    workOrders.forEach(wo => {
      const customerName = wo.header?.customer_name || "";
      const itemName = wo.header?.item_name || "";
      const reelSize = wo.header?.reel_size || "";
      const cuttingSize = wo.header?.cutting_size || "";
      const woNumber = wo.header?.work_order_number || wo.id;
      const woDate = wo.header?.work_order_creation_date || "";
      const woId = wo.id;
      
      // Get RM info from processes (correct nested structure)
      const processes = wo.processes || {};
      
      // Process 1 - Corrugation (has rows with base_reel_id and flute_reel_id)
      if (processes.process1) {
        const p1 = processes.process1;
        const rows = p1.rows || [];
        rows.forEach(row => {
          if (row.base_reel_id) {
            usageList.push({
              customer_name: customerName,
              item_name: itemName,
              rm_name: row.base_reel_id,
              size: reelSize || cuttingSize || "-",
              wo_number: woNumber,
              wo_id: woId,
              wo_date: woDate,
              qty: parseFloat(row.base_weight) || 0
            });
          }
          if (row.flute_reel_id) {
            usageList.push({
              customer_name: customerName,
              item_name: itemName,
              rm_name: row.flute_reel_id,
              size: reelSize || cuttingSize || "-",
              wo_number: woNumber,
              wo_id: woId,
              wo_date: woDate,
              qty: parseFloat(row.flute_weight) || 0
            });
          }
        });
      }
      
      // Process 2 - Sheet Cutting (has reel_no)
      if (processes.process2?.reel_no) {
        usageList.push({
          customer_name: customerName,
          item_name: itemName,
          rm_name: processes.process2.reel_no,
          size: reelSize || cuttingSize || "-",
          wo_number: woNumber,
          wo_id: woId,
          wo_date: woDate,
          qty: parseFloat(wo.header?.required_quantity) || 0
        });
      }
    });
    
    return usageList;
  };

  const materialUsageData = useMemo(() => getMaterialUsageData(), [workOrders]);

  // Get unique values for dropdowns
  const uniqueCompanies = useMemo(() => 
    [...new Set(materialUsageData.map(m => m.customer_name).filter(Boolean))].sort(),
    [materialUsageData]
  );
  
  const uniqueRmNames = useMemo(() => 
    [...new Set(materialUsageData.map(m => m.rm_name).filter(Boolean))].sort(),
    [materialUsageData]
  );
  
  const uniqueSizes = useMemo(() => 
    [...new Set(materialUsageData.map(m => m.size).filter(s => s && s !== "-"))].sort(),
    [materialUsageData]
  );

  // Get unique item names - filtered by selected company if any
  const uniqueItemNames = useMemo(() => {
    if (selectedCompany) {
      // Get items from the selected customer in database
      const customer = customers.find(c => c.company_name === selectedCompany);
      if (customer && customer.items && customer.items.length > 0) {
        return customer.items.map(item => item.item_name).filter(Boolean).sort();
      }
      // Fallback to items from work orders for this company
      return [...new Set(materialUsageData
        .filter(m => m.customer_name === selectedCompany)
        .map(m => m.item_name)
        .filter(Boolean)
      )].sort();
    }
    // No company selected - show all items from work orders
    return [...new Set(materialUsageData.map(m => m.item_name).filter(Boolean))].sort();
  }, [materialUsageData, selectedCompany, customers]);

  // Calculate stats
  const stats = useMemo(() => {
    const totalQty = materialUsageData.reduce((sum, m) => sum + (m.qty || 0), 0);
    return {
      totalWorkOrders: workOrders.length,
      totalCompanies: uniqueCompanies.length,
      uniqueMaterials: uniqueRmNames.length,
      uniqueSizes: uniqueSizes.length,
      uniqueItems: uniqueItemNames.length,
      totalQty
    };
  }, [workOrders, materialUsageData, uniqueCompanies, uniqueRmNames, uniqueSizes, uniqueItemNames]);

  // Filter data
  const filteredData = useMemo(() => {
    return materialUsageData.filter(item => {
      if (selectedCompany && item.customer_name !== selectedCompany) return false;
      if (selectedRmName && item.rm_name !== selectedRmName) return false;
      if (selectedSize && item.size !== selectedSize) return false;
      if (selectedItemName && item.item_name !== selectedItemName) return false;
      return true;
    });
  }, [materialUsageData, selectedCompany, selectedRmName, selectedSize, selectedItemName]);

  const hasFilters = selectedCompany || selectedRmName || selectedSize || selectedItemName;

  // Navigate to work order view
  const handleWorkOrderClick = (woId) => {
    navigate(`/workorder/view/${woId}`);
  };

  // Searchable Combobox Component
  const SearchableCombobox = ({ open, setOpen, value, setValue, options, placeholder, icon: Icon }) => (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-black/50 border-gray-600/30 text-white hover:bg-cyan-600/10"
        >
          <span className="flex items-center gap-2 truncate">
            <Icon className="h-4 w-4 text-cyan-400 shrink-0" />
            {value || placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0 bg-gray-900 border-gray-600/30" align="start">
        <Command className="bg-transparent">
          <CommandInput placeholder={`Search ${placeholder.toLowerCase()}...`} className="text-white" />
          <CommandList>
            <CommandEmpty className="text-gray-400 py-2 text-center text-sm">No results found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  setValue("");
                  setOpen(false);
                }}
                className="text-gray-400 hover:bg-cyan-600/20"
              >
                <Check className={cn("mr-2 h-4 w-4", !value ? "opacity-100" : "opacity-0")} />
                All
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={option}
                  onSelect={() => {
                    setValue(option);
                    setOpen(false);
                  }}
                  className="text-white hover:bg-cyan-600/20"
                >
                  <Check className={cn("mr-2 h-4 w-4", value === option ? "opacity-100" : "opacity-0")} />
                  {option}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/workorder#saved")}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-black text-cyan-400 tracking-wider flex items-center gap-3">
                <Layers className="h-8 w-8" />
                Material Usage Report
              </h1>
              <p className="text-gray-400 mt-1">Track raw material usage across work orders</p>
            </div>
          </div>
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="border-gray-600/50 text-cyan-400 hover:bg-cyan-600/20"
            disabled={refreshing}
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards - Top of Page */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-yellow-900/40 to-yellow-800/20 border-2 border-gray-600/30">
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center gap-1">
                <FileText className="h-6 w-6 text-cyan-400" />
                <p className="text-2xl font-black text-cyan-400">{stats.totalWorkOrders}</p>
                <p className="text-xs font-medium text-cyan-300/70">Total Work Orders</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-900/40 to-blue-800/20 border-2 border-blue-500/30">
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center gap-1">
                <Building2 className="h-6 w-6 text-blue-400" />
                <p className="text-2xl font-black text-blue-400">{stats.totalCompanies}</p>
                <p className="text-xs font-medium text-blue-300/70">Companies</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-900/40 to-green-800/20 border-2 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center gap-1">
                <Package className="h-6 w-6 text-green-400" />
                <p className="text-2xl font-black text-green-400">{stats.uniqueMaterials}</p>
                <p className="text-xs font-medium text-green-300/70">Unique Materials</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 border-2 border-purple-500/30">
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center gap-1">
                <Ruler className="h-6 w-6 text-purple-400" />
                <p className="text-2xl font-black text-purple-400">{stats.uniqueSizes}</p>
                <p className="text-xs font-medium text-purple-300/70">Unique Sizes</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-900/40 to-orange-800/20 border-2 border-orange-500/30">
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center gap-1">
                <ShoppingBag className="h-6 w-6 text-orange-400" />
                <p className="text-2xl font-black text-orange-400">{stats.uniqueItems}</p>
                <p className="text-xs font-medium text-orange-300/70">Unique Items</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-pink-900/40 to-pink-800/20 border-2 border-pink-500/30">
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center gap-1">
                <TrendingUp className="h-6 w-6 text-pink-400" />
                <p className="text-2xl font-black text-pink-400">{stats.totalQty.toFixed(0)}</p>
                <p className="text-xs font-medium text-pink-300/70">Total Qty Used</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search Filters */}
        <Card className="bg-black/60 border-2 border-gray-600/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-cyan-400 flex items-center gap-2 text-lg">
                <Filter className="h-5 w-5" />
                Search Filters
              </CardTitle>
              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Company Filter */}
              <div className="space-y-2">
                <Label className="text-cyan-300 text-sm flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Company Name
                </Label>
                <SearchableCombobox
                  open={companyOpen}
                  setOpen={setCompanyOpen}
                  value={selectedCompany}
                  setValue={(val) => {
                    setSelectedCompany(val);
                    setSelectedItemName(""); // Clear item when company changes
                  }}
                  options={uniqueCompanies}
                  placeholder="Select Company"
                  icon={Building2}
                />
              </div>

              {/* Item Name Filter */}
              <div className="space-y-2">
                <Label className="text-cyan-300 text-sm flex items-center gap-2">
                  <ShoppingBag className="h-4 w-4" />
                  Item Name
                </Label>
                <SearchableCombobox
                  open={itemNameOpen}
                  setOpen={setItemNameOpen}
                  value={selectedItemName}
                  setValue={setSelectedItemName}
                  options={uniqueItemNames}
                  placeholder="Select Item"
                  icon={ShoppingBag}
                />
              </div>

              {/* RM Name Filter */}
              <div className="space-y-2">
                <Label className="text-cyan-300 text-sm flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  RM Name
                </Label>
                <SearchableCombobox
                  open={rmNameOpen}
                  setOpen={setRmNameOpen}
                  value={selectedRmName}
                  setValue={setSelectedRmName}
                  options={uniqueRmNames}
                  placeholder="Select Material"
                  icon={Package}
                />
              </div>

              {/* Size Filter */}
              <div className="space-y-2">
                <Label className="text-cyan-300 text-sm flex items-center gap-2">
                  <Ruler className="h-4 w-4" />
                  Size
                </Label>
                <SearchableCombobox
                  open={sizeOpen}
                  setOpen={setSizeOpen}
                  value={selectedSize}
                  setValue={setSelectedSize}
                  options={uniqueSizes}
                  placeholder="Select Size"
                  icon={Ruler}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Material Usage Table */}
        <Card className="bg-black/60 border-2 border-gray-600/50">
          <CardHeader>
            <CardTitle className="text-cyan-400 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Material Usage Details
              <span className="ml-2 px-3 py-1 bg-cyan-600/20 text-cyan-300 rounded-full text-sm">
                {filteredData.length} records
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-400">Loading data...</div>
            ) : filteredData.length === 0 ? (
              <div className="text-center py-12">
                <Layers className="h-16 w-16 mx-auto mb-4 text-gray-500" />
                <p className="text-xl font-bold text-gray-400 mb-2">No Material Usage Found</p>
                <p className="text-gray-500">
                  {hasFilters 
                    ? "Try adjusting your search filters" 
                    : "Create work orders to track material usage"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-gray-900 z-10">
                    <TableRow className="border-gray-600/30 hover:bg-transparent">
                      <TableHead className="text-cyan-300">Company Name</TableHead>
                      <TableHead className="text-cyan-300">Item Name</TableHead>
                      <TableHead className="text-cyan-300">RM Name</TableHead>
                      <TableHead className="text-cyan-300">Size</TableHead>
                      <TableHead className="text-cyan-300">Work Order</TableHead>
                      <TableHead className="text-cyan-300 text-right">Qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((item, idx) => (
                      <TableRow 
                        key={idx} 
                        className="border-gray-600/20 hover:bg-cyan-600/10"
                      >
                        <TableCell>
                          <button
                            onClick={() => {
                              setSelectedCompany(item.customer_name);
                              setCompanyOpen(false);
                            }}
                            className="font-medium text-white hover:text-cyan-400 transition-colors text-left"
                          >
                            {item.customer_name || "-"}
                          </button>
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => {
                              setSelectedItemName(item.item_name);
                              setItemNameOpen(false);
                            }}
                            className="text-gray-300 hover:text-cyan-400 transition-colors text-left"
                          >
                            {item.item_name || "-"}
                          </button>
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => {
                              setSelectedRmName(item.rm_name);
                              setRmNameOpen(false);
                            }}
                            className="text-cyan-400 font-mono hover:text-gray-300 transition-colors text-left"
                          >
                            {item.rm_name || "-"}
                          </button>
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => {
                              if (item.size && item.size !== "-") {
                                setSelectedSize(item.size);
                                setSizeOpen(false);
                              }
                            }}
                            className="text-gray-300 hover:text-cyan-400 transition-colors text-left"
                          >
                            {item.size || "-"}
                          </button>
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => handleWorkOrderClick(item.wo_id)}
                            className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded text-sm font-bold hover:bg-blue-500/40 transition-colors"
                          >
                            {item.wo_number}
                          </button>
                        </TableCell>
                        <TableCell className="text-right font-bold text-white">
                          {item.qty?.toFixed(0) || 0}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WOReports;
